import {
  Button, CIActionBtnGroup, CIInfoIcon, Dropdown,
  Input,
  MultiSelectDropdown,
  MyIcon
} from 'ccs-ui-components';
import { Dispatch, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IExceptionModel, IIngressExceptionModel } from '../../../../../../models/index';
import { useSelector } from '../../../../../../store/AppStore';
import { checkForExceptionExist, getActionsForException, getAssociatedActionsForSelectedException, getErrorCodeForSelectedException, getExceptionActions, getExceptionName } from '../../../../../../utilities/services/ServiceEventAssociationUtils';
import {
  IExceptionAction,
  IExceptionState,
  INGRESS_EVENT_FORM_ACTIONS
} from '../service-event-state-manager/ServiceEventStateManager';
import classes from './IngressEventAssociationForm.module.scss';

interface IAddExceptionViewProps {
  addEventClicked: boolean;
  editIngressEventButtonClick: boolean;
  viewMode: boolean;
  serviceOperationName: string;
  isCancelAddIngressEventClick: boolean;
  formState: IExceptionState;
  dispatchExceptionState: Dispatch<IExceptionAction>;
}

/**
 * View to add exceptions in service event association
 * @param param0: props
 * @returns: View
 */
const AddExceptionView = ({ addEventClicked,
  editIngressEventButtonClick,
  viewMode,
  serviceOperationName,
  isCancelAddIngressEventClick,
  formState,
  dispatchExceptionState
}: IAddExceptionViewProps) => {
  const { t: translate } = useTranslation();

  const [selectedExceptionIndex, setSelectedExceptionIndex] = useState<string>('');
  const [selectedException, setSelectedException] = useState('');
  const { exceptions } = useSelector((state) => state.exceptionReducer);
  const { ingressEventMappingList } = useSelector(
    (state) => state.ingressEventMappingReducer
  );

  // Multiselect states
  const [action, setData] = useState<string[]>(getExceptionActions(selectedException, exceptions));
  const [all, setAll] = useState<boolean>(false);
  const handleData = (args: string[]) => setData(args);
  const handleAll = (arg: boolean) => setAll(arg);

  useEffect(() => {
    const exceptionList: IExceptionModel[] = exceptions.map(
      (exception) => exception
    );
    if (exceptionList.length && exceptionList[0].action) {
      setData(getExceptionActions(selectedException, exceptions));
    }
  }, [selectedException]);

  useEffect(() => {
    // if add event button clicked, clear the exception list
    if (addEventClicked || isCancelAddIngressEventClick) {
      dispatchExceptionState({
        type: INGRESS_EVENT_FORM_ACTIONS.CLEAR_EXCEPTIONS
      });
    }

    // if edit event button was click then get data from store and set it to the local variables
    if (editIngressEventButtonClick || viewMode) {
      const editedEvent = ingressEventMappingList.filter(
        (item) => item.serviceOperationName === serviceOperationName
      );
      dispatchExceptionState({
        type: INGRESS_EVENT_FORM_ACTIONS.ADD_ALL_EXCEPTION_CLICKED,
        addAllExceptionPayload: { exceptions: editedEvent[0]?.exceptions }
      });
    }
  }, [addEventClicked, editIngressEventButtonClick, isCancelAddIngressEventClick]);

  /**
   * handles the type change for exception dropdown
   */
  const exceptionTypeChangeHandler = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const index = parseInt(e.target.value, 10);
    setSelectedExceptionIndex(e.target.value);
    setSelectedException(getExceptionName(exceptions)[index].name);
  };

  /**
   * Calls when add exception button clicked
   *
   * save the exceptions temporary in the state
   */
  const onAddExceptionClicked = () => {
    const actionArray = getActionsForException(
      formState.exceptionList,
      selectedException
    );
    if (actionArray.length) {
      dispatchExceptionState({
        type: INGRESS_EVENT_FORM_ACTIONS.DELETE_DUPLICATE_EXCEPTION,
        deleteDuplicateException: { exceptionId: selectedException }
      });
    }
    const exception: IIngressExceptionModel = {
      exceptionId: selectedException,
      errorCode: `${getErrorCodeForSelectedException(selectedException, exceptions)}`,
      action: Array.from(new Set([...actionArray, ...action]))
    };
    dispatchExceptionState({
      type: INGRESS_EVENT_FORM_ACTIONS.ADD_EXCEPTION_CLICKED,
      addExceptionPayload: { exception }
    });
    setSelectedException('');
    setSelectedExceptionIndex('');
  };

  /**
   * Calls when delete exception button clicked
   * @param index : index number on which exception to be deleted
   */
  const onDeleteExceptionClicked = (index: number) => {
    dispatchExceptionState({
      type: INGRESS_EVENT_FORM_ACTIONS.DELETE_EXCEPTION_CLICKED,
      deleteExceptionPayload: { index }
    });
  };

  /**
   * Call when edit exception button was clicked
   * @param exceptionIndex: edited exception index
   */
  const onEditExceptionClicked = (exceptionIndex: number) => {
    dispatchExceptionState({
      type: INGRESS_EVENT_FORM_ACTIONS.EDIT_EXCEPTION_CLICKED,
      editExceptionPayload: {
        isEditButtonClick: true,
        editIndex: exceptionIndex
      }
    });
    const exception = formState.exceptionList[exceptionIndex || 0];
    const index = getExceptionName(exceptions).findIndex(
      (item) => item.name === exception.exceptionId
    );
    setSelectedExceptionIndex(`${index}`);
    setSelectedException(exception.exceptionId);
    setData(exception.action);
  };

  /**
   * Calls when cancel edit exception button was clicked
   */
  const onCancelEditExceptionClicked = () => {
    dispatchExceptionState({
      type: INGRESS_EVENT_FORM_ACTIONS.EDIT_EXCEPTION_CLICKED,
      editExceptionPayload: { isEditButtonClick: false, editIndex: undefined }
    });
  };

  /**
   * Calls when update exception button was clicked
   */
  const onUpdateExceptionClicked = () => {
    const newException: IIngressExceptionModel = {
      exceptionId: selectedException,
      errorCode: `${getErrorCodeForSelectedException(selectedException, exceptions)}`,
      action
    };

    dispatchExceptionState({
      type: INGRESS_EVENT_FORM_ACTIONS.UPDATE_EXCEPTION_CLICKED,
      updateExceptionPayload: {
        editIndex: formState.editIndex ?? 0,
        exception: newException
      }
    });
    setSelectedExceptionIndex('');
    setSelectedException('');
    setData(getExceptionActions(selectedException, exceptions));
  };

  const EditExceptionForm = () =>
    useMemo(
      () => (
        <div>
          <Row className={classes['no-records']}>
            <Col sm={4}>
              <div className={classes['status-dropdown']}>
                <Dropdown
                  label=""
                  labelText=""
                  data={getExceptionName(exceptions)}
                  className={classes['dropdown-control']}
                  value={selectedExceptionIndex}
                  onChange={exceptionTypeChangeHandler}
                  data-testid="edit-exception-id"
                />
              </div>
            </Col>
            <Col sm={3}>
              <div className={classes['exception-error-code']}>
                <Input
                  label=""
                  aria-label="Error Code"
                  name=""
                  disabled
                  value={getErrorCodeForSelectedException(selectedException, exceptions)}
                />
              </div>
            </Col>
            <Col sm={4}>
              <div className={classes['status-dropdown']}>
                <MultiSelectDropdown
                  options={getAssociatedActionsForSelectedException(selectedException, exceptions)}
                  onSelect={handleData}
                  selectedOptions={action}
                  selectAll={all}
                  handleSelectAll={handleAll}
                  height="38px"
                  data-testid="exception-action"
                />
              </div>
            </Col>
            <Col sm={1}>
              <div className={classes['item-btn-container']}>
                <Button
                  className={classes['update-button']}
                  onClick={onUpdateExceptionClicked}
                  disabled={checkForExceptionExist(selectedException, exceptions, action, formState)}
                  variant="tertiary"
                >
                  <MyIcon name="thinCheckIcon" />
                </Button>
                <Button
                  onClick={onCancelEditExceptionClicked}
                  data-testid="btn-cancel-edit-exception"
                  variant="tertiary"
                  className={classes['cancel-button']}
                >
                  <MyIcon name="xMarkLargeIcon" />
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      ),
      []
    );

  /**
 * View for Adding the exceptions for an ingress event
 */
  const ExceptionForm = () =>
    useMemo(
      () => (
        <div>
          <Row className={classes['list-header']}>
            <Col sm={4}>{translate('service_exceptions.exception_name')} <CIInfoIcon iconTxt={translate('service_exceptions.exception_name_info_bubble')} /></Col>
            <Col sm={3}>{translate('service_exceptions.error_code')} <CIInfoIcon iconTxt={translate('service_exceptions.error_code_info_bubble')} /></Col>
            <Col sm={3}>{translate('service_exceptions.action')} <CIInfoIcon iconTxt={translate('service_exceptions.action_info_bubble')} /></Col>
          </Row>
          <div className={classes.separator} />
          <Row className={classes['no-records']}>
            <Col sm={4}>
              <div className={classes['status-dropdown']}>
                <Dropdown
                  label=""
                  labelText=""
                  data={formState.isEditExceptionClicked ? [] : getExceptionName(exceptions)}
                  className={classes['dropdown-control']}
                  value={selectedExceptionIndex}
                  onChange={exceptionTypeChangeHandler}
                  disabled={formState.isEditExceptionClicked}
                  data-testid="edit-exception-id"
                  placeholder={translate('fsm_registry_form.lbl_placeholder_select') ?? 'Select'}
                />
              </div>
            </Col>
            <Col sm={3}>
              <Input
                className={classes['input-field']}
                label=""
                name=""
                aria-label="Error Code"
                disabled
                value={
                  formState.isEditExceptionClicked
                    ? ''
                    : `${getErrorCodeForSelectedException(selectedException, exceptions)}`
                }
              />
            </Col>
            <Col sm={3}>
              <div className={classes['status-dropdown']}>
                <MultiSelectDropdown
                  options={
                    formState.isEditExceptionClicked
                      ? []
                      : getAssociatedActionsForSelectedException(selectedException, exceptions)
                  }
                  onSelect={handleData}
                  selectedOptions={formState.isEditExceptionClicked ? [] : action}
                  selectAll={formState.isEditExceptionClicked ? false : all}
                  handleSelectAll={handleAll}
                  height="38px"
                  data-testid="select-action"
                  placeHolder={String(translate('fsm_registry_form.lbl_placeholder_select'))}

                />
              </div>
            </Col>
            <Col sm={2}>
              <Button
                className={classes['add-exception-button']}
                onClick={onAddExceptionClicked}
                disabled={
                  checkForExceptionExist(selectedException, exceptions, action, formState) || formState.isEditExceptionClicked
                }
                data-testid="btn-add-exception"
                variant="secondary"
              >
                {translate('service_exceptions.add_button')}
              </Button>
            </Col>
          </Row>
        </div>
      ),
      []
    );

  return (
    <div className={classes['ingress-exception-list']}>
      <ExceptionForm />
      {formState.exceptionList.length > 0
        && formState.exceptionList.map((item, index) => (
          <div key={item.exceptionId + item.action}>
            {formState.editIndex !== index && (
              <Row
                className={classes['no-records']}
                key={item.exceptionId + item.errorCode + item.action}
              >
                <div className={classes['exception-container']}>
                  <Col sm={4} className={classes['exception-name']}>
                    {item.exceptionId}
                  </Col>
                  <Col sm={3} className={classes['error-code']}>
                    {item.errorCode}
                  </Col>
                  <Col sm={4} className={classes.actions}>
                    {item.action.toString()}
                  </Col>
                  <Col sm={1} className={classes.buttons}>
                    <CIActionBtnGroup
                      buttons={[
                        {
                          label: 'edit',
                          key: 'editIcon',
                          variant: 'tertiary',
                          onClick: async () => onEditExceptionClicked(index),
                          testId: 'btn-edit-exception'
                        },
                        {
                          label: 'delete',
                          key: 'deleteIcon',
                          variant: 'tertiary',
                          disabled: formState.isEditExceptionClicked,
                          onClick: async () => onDeleteExceptionClicked(index),
                          testId: 'btn-delete-exception'
                        }]}
                    />
                  </Col>
                </div>
              </Row>
            )}
            <Row>
              {formState.editIndex === index
                && formState.isEditExceptionClicked && <EditExceptionForm />}
            </Row>
          </div>
        ))}
    </div>
  );
};

export default AddExceptionView;
